import { createSelector } from 'reselect';
import { getFacilityViewPixel } from '@sparefoot/react-core/utils/tracking';
import { get, reduce, omit } from 'lodash';
import { getQueryParams } from 'store/selectors/application';
import { storageStackedUnitBuckets } from 'config/units';

import { getScarcityFlag } from 'utils/store/getScarcityFlag';

export const getFacilityFromRoute = createSelector(
	(state, props) => get(props, 'match.params.facilityId'),
	(state) => state.facilities.byId,
	(facilityId, facilities) => facilities[facilityId],
);

export const getNearbyFacilities = createSelector(
	getFacilityFromRoute,
	(state) => state.facilities.byId,
	(state) => state.units.byId,
	(facility, facilities, units) => {
		const nearbyFacilities = get(facility, 'nearbyFacilities', []).map(
			(facilityId, index) => {
				const nearbyFacility = facilities[facilityId];
				const facilityUnits = get(nearbyFacility, 'units', {});
				const unitBuckets = reduce(
					storageStackedUnitBuckets,
					(allUnits, size) => ({
						...allUnits,
						[size]: units[facilityUnits[size]],
					}),
					{},
				);

				return {
					...nearbyFacility,
					index: index + 1,
					position: index + 1,
					units: {
						...facilityUnits,
						...unitBuckets,
					},
				};
			},
		);
		return nearbyFacilities;
	},
);

export const getAddFacilityData = createSelector(
	(state) => state.app.baseUrl,
	getQueryParams,
	(baseUrl, queryParams) => ({
		submitSuccess: get(queryParams, 'submitSuccess', false),
		canonical: `${baseUrl}/add-facility`,
	}),
);

export default createSelector(
	getFacilityFromRoute,
	getQueryParams,
	getNearbyFacilities,
	(state) => state.facilities.byId,
	(state) => state.facilities.loading,
	(state) => state.facilities.redirectURL,
	(state) => state.facilities.statusCode,
	(state) => state.units.byId,
	(state) => state.reviews.byId,
	(state) => state.app.baseUrl,
	(
		facility,
		queryParams,
		nearbyFacilities,
		facilities,
		loading,
		redirectURL,
		statusCode,
		units,
		reviews,
		baseUrl,
	) => {
		const location = get(facility, 'location');
		const groupedAmenities = get(facility, 'groupedAmenities');
		const storageUnits = get(facility, 'units.storageUnits', []).map(
			(unitId) => units[unitId],
		);
		const vehicleUnits = get(facility, 'units.vehicleUnits', []).map(
			(unitId) => units[unitId],
		);

		return {
			facility,
			baseUrl,
			nearbyFacilities,
			loading,
			redirectURL,
			statusCode,
			nearbyFacilitiesSearchId: get(facility, 'nearbyFacilitiesSearchId'),
			amenities: omit(
				groupedAmenities,
				!get(facility, 'units.vehicleUnits', []).length
					? ['vehicle']
					: [],
			),
			meta: {
				...get(facility, 'meta', {}),
				title: `${facility?.meta?.title} - SelfStorage.com`,
				canonical: baseUrl + facility?.meta?.canonical,
			},
			reviewPageNum: parseInt(get(queryParams, 'reviewPage', 1)),
			reviewSource: get(queryParams, 'rsrc'),
			phone: get(facility, 'phone'),
			searchLocation: location
				? `${location.streetAddress1}, ${location.city}, ${location.state} ${location.zip}`
				: '',
			storageUnits,
			vehicleUnits,
			reviews: get(facility, 'userReviews', []).map(
				(reviewId) => reviews[reviewId],
			),
			trackingPixel: getFacilityViewPixel({
				facility: get(facility, 'id'),
			}),
			scarcityShow: getScarcityFlag(units),
		};
	},
);
